import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './styles/index.scss';
import './i18n';
import { languages, getBestMatchLanguage } from './i18n';
import { useTranslation } from 'react-i18next';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import GoogleTagManager from './components/GoogleTagManager';

const TrailingSlashRedirect = () => {
  const location = useLocation();
  if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
    return (
      <Navigate
        to={`${location.pathname.replace(/\/+$/, '')}${location.search}${location.hash}`}
        replace
      />
    );
  }
  return null;
};

/** SPA redirects must keep Ads click IDs (gclid/wbraid) and UTM params. */
const PreserveQueryNavigate = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

/** Legacy news CTAs pointed at /mental-health (404). Send users to the web app. */
const MentalHealthChatRedirect = () => {
  const location = useLocation();
  useEffect(() => {
    const dest = `https://chat.psyhelp.info${location.search || ''}${location.hash || ''}`;
    window.location.replace(dest);
  }, [location.hash, location.search]);
  return (
    <div className="loading" role="status">
      Redirecting…
    </div>
  );
};

const NewsPage = React.lazy(() => import('./pages/NewsPage'));
const NewsDetailPage = React.lazy(() => import('./pages/NewsDetailPage'));
const VideosPage = React.lazy(() => import('./pages/VideosPage'));
const CorporatePage = React.lazy(() => import('./pages/CorporatePage'));
const NGOPage = React.lazy(() => import('./pages/NGOPage'));
const WebinarRegisterPage = React.lazy(() => import('./pages/WebinarRegisterPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const AccountDeletionPage = React.lazy(() => import('./pages/AccountDeletionPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = React.lazy(() => import('./pages/TermsOfUsePage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const DashboardOceanicClientPage = React.lazy(() => import('./pages/DashboardOceanicClientPage'));
const DashboardOceanicInternalPage = React.lazy(() => import('./pages/DashboardOceanicInternalPage'));
const CorporateHrDashboardPage = React.lazy(() => import('./pages/CorporateHrDashboardPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

const CookiesPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.CookiesPage }))
);
const ClinicalSafetyPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.ClinicalSafetyPage }))
);
const EditorialPolicyPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.EditorialPolicyPage }))
);
const TeamPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.TeamPage }))
);
const ResearchPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.ResearchPage }))
);
const SecurityPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.SecurityPage }))
);
const AiWellbeingPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.AiWellbeingPage }))
);
const SocialMissionPage = React.lazy(() =>
  import('./pages/TrustPages').then((mod) => ({ default: mod.SocialMissionPage }))
);
const ForSpecialistsPage = React.lazy(() => import('./pages/ForSpecialistsPage'));
const TherapistApplicationsAdminPage = React.lazy(() =>
  import('./pages/TherapistApplicationsAdminPage')
);

const LanguageRedirect = () => {
  const location = useLocation();
  const targetLang = getBestMatchLanguage();
  return <Navigate to={`/${targetLang}${location.search}${location.hash}`} replace />;
};

const DashboardWrapper = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== 'en') i18n.changeLanguage('en');
  }, [i18n]);
  return <DashboardPage />;
};

/** Legacy /ua… → /uk… (keep query for gclid/UTM). */
const UaToUkRedirect = () => {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/ua(?=\/|$)/, '') || '';
  return <Navigate to={`/uk${rest}${location.search}${location.hash}`} replace />;
};

const LanguageWrapper = () => {
  const { language } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (languages.includes(language) && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  if (language === 'ua') {
    const rest = location.pathname.replace(/^\/ua(?=\/|$)/, '') || '';
    return <Navigate to={`/uk${rest}${location.search}${location.hash}`} replace />;
  }

  if (!languages.includes(language)) {
    return <PreserveQueryNavigate to={`/${getBestMatchLanguage()}`} />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/page/:page" element={<NewsPage />} />
        <Route path="/news/:id" element={<NewsDetailPage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/corporate" element={<CorporatePage />} />
        <Route path="/ngo" element={<NGOPage />} />
        <Route path="/webinars" element={<WebinarRegisterPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy-policy" element={<PreserveQueryNavigate to={`/${language}/privacy`} />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/terms-of-use" element={<PreserveQueryNavigate to={`/${language}/terms`} />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/clinical-safety" element={<ClinicalSafetyPage />} />
        <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/research" element={<ResearchPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/ai-wellbeing" element={<AiWellbeingPage />} />
        <Route path="/social-mission" element={<SocialMissionPage />} />
        <Route path="/for-specialists" element={<ForSpecialistsPage />} />
        <Route path="/careers" element={<PreserveQueryNavigate to={`/${language}/for-specialists`} />} />
        <Route path="/mental-health" element={<MentalHealthChatRedirect />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <TrailingSlashRedirect />
        <GoogleTagManager />
        <Suspense fallback={<LocalizedLoading />}>
          <Routes>
            <Route path="/dashboard" element={<DashboardWrapper />} />
            <Route path="/dashboard/client" element={<DashboardOceanicClientPage />} />
            <Route path="/dashboard/internal" element={<DashboardOceanicInternalPage />} />
            <Route path="/dashboard/hr" element={<CorporateHrDashboardPage />} />
            <Route path="/admin/therapist-applications" element={<TherapistApplicationsAdminPage />} />
            <Route path="/anxiety-support" element={<PreserveQueryNavigate to="/en/news" />} />
            <Route path="/business-solutions" element={<PreserveQueryNavigate to="/en/corporate" />} />
            <Route path="/careers" element={<PreserveQueryNavigate to={`/${getBestMatchLanguage()}/for-specialists`} />} />
            <Route path="/mental-health" element={<MentalHealthChatRedirect />} />
            <Route path="/ua" element={<UaToUkRedirect />} />
            <Route path="/ua/*" element={<UaToUkRedirect />} />
            <Route path="/" element={<LanguageRedirect />} />
            <Route path="/:language/*" element={<LanguageWrapper />} />
            <Route path="*" element={<PreserveQueryNavigate to="/" />} />
          </Routes>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
}

function LocalizedLoading() {
  const { t } = useTranslation();
  return (
    <div className="loading" role="status">
      {t('common.loading', 'Loading...')}
    </div>
  );
}

export default App;
